import React, { useCallback, useReducer } from 'react';

type Variant = 'error' | 'success' | 'info' | 'warning';
type ToastAction = ReturnType<typeof clearToast | typeof displayToast>;

const initialState = {
  variant: 'error' as Variant,
  show: false,
  message: '',
};

const ToastStateContext = React.createContext<typeof initialState>(undefined!);
const ToastDispatchContext = React.createContext<React.Dispatch<ToastAction>>(
  undefined!
);

function useToastState() {
  const context = React.useContext(ToastStateContext);
  if (context === undefined) {
    throw new Error(`useToastState must be used within a NotificationProvider`);
  }
  return context;
}

function notificationReducer(state: typeof initialState, action: ToastAction) {
  switch (action.type) {
    case 'SET_NOTIFICATION':
      return {
        ...state,
        variant: action.variant,
        show: true,
        message: action.message,
      };
    case 'CLEAR_NOTIFICATION':
      return { ...state, show: false };
    default: {
      return state;
    }
  }
}

function displayToast(message: string, variant: Variant) {
  return {
    type: 'SET_NOTIFICATION',
    message,
    variant,
  } as const;
}

function clearToast() {
  return {
    type: 'CLEAR_NOTIFICATION',
  } as const;
}

function useToast() {
  const dispatch = React.useContext(ToastDispatchContext);
  if (dispatch === undefined) {
    throw new Error(`useToast must be used within a ToastProvider`);
  }
  return {
    clearToast: useCallback(
      function() {
        dispatch(clearToast());
      },
      [dispatch]
    ),
    errorToast: useCallback(
      function(message: string) {
        dispatch(displayToast(message, 'error'));
      },
      [dispatch]
    ),
    successToast: useCallback(
      function(message: string) {
        dispatch(displayToast(message, 'success'));
      },
      [dispatch]
    ),
    warningToast: useCallback(
      function(message: string) {
        dispatch(displayToast(message, 'warning'));
      },
      [dispatch]
    ),
    infoToast: useCallback(
      function(message: string) {
        dispatch(displayToast(message, 'info'));
      },
      [dispatch]
    ),
  };
}

// React docs suggest using two separate contexts when using useReducer with Context
// https://reactjs.org/docs/hooks-faq.html#how-to-avoid-passing-callbacks-down
const ToastProvider: React.FunctionComponent = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  return (
    <ToastStateContext.Provider value={state}>
      <ToastDispatchContext.Provider value={dispatch}>
        {children}
      </ToastDispatchContext.Provider>
    </ToastStateContext.Provider>
  );
};

export { ToastProvider, useToast, useToastState };
